/* oxlint-disable max-lines-per-function */
/* oxlint-disable complexity */

import { useMemo } from 'react';

import { getMediaCount } from '@change-corgi/core/features/sapRichMedia';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { Draft } from '#src/app/pages/startAPetitionV2/pageContext';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useCurrentAuthenticatedUser } from '#src/app/shared/hooks/session';
import { googleTagManagerTracking } from '#src/app/shared/utils/googleTagManagerTracking';
import { isFbLoggedIn } from '#src/app/shared/utils/loginOrJoin';

import type { PublishedPetitionResponse } from './api/createPublishedPetition/types';
import { compareNormalizedStrings } from './utils';

type TrackPrePublishActivatePetitionArgs = {
	petition: PublishedPetitionResponse;
	draft: Draft | undefined | null;
	// Resolved at publish (metadata-derived for social-starter drafts, URL param otherwise) so the
	// tracked source_location matches what was actually published rather than the lossy URL value.
	sourceLocation: string | undefined;
};
type TrackConversionArgs = { petition: PublishedPetitionResponse };

// Maps the inference scope enum to geo_reach's lowercase values so inferred_geo_scope and the
// starter-entered geo_reach are directly comparable on activate_petition (CHANGE-97720).
const SCOPE_TO_GEO_REACH = { LOCAL: 'local', NATIONAL: 'national', GLOBAL: 'global' } as const;

type UsePrePublishTrackingReturn = {
	trackPrePublishActivatePetition: (args: TrackPrePublishActivatePetitionArgs) => void;
	trackConversion: (args: TrackConversionArgs) => void;
};

export function usePrePublishTracking(): UsePrePublishTrackingReturn {
	const track = useTracking();
	const userHandle = useQueryParamValue('user_handle');
	const user = useCurrentAuthenticatedUser();
	const utilityContext = useUtilityContext();

	return useMemo(
		() => ({
			trackPrePublishActivatePetition: ({ petition, draft, sourceLocation }) => {
				const mediaCount = getMediaCount(petition.description ?? '');
				const totalMediaCount = Object.values(mediaCount).reduce<number>((sum, count) => sum + count, 0);

				const bodyImages = mediaCount.image;
				const bodyYoutube = mediaCount.youtube;

				// Background scope/location inference (CHANGE-97720), stashed on the draft in the AI flow.
				// Map to geo_reach's lowercase local/national/global so inferred_geo_scope compares directly.
				const inferred = draft?.metadata?.inferredScopeLocation;
				const inferredGeoScope = inferred ? SCOPE_TO_GEO_REACH[inferred.scope] : undefined;

				void track('activate_petition', {
					// user input
					issue_prompt: draft?.input?.issuePrompt ?? undefined,
					personal_story: draft?.input?.personalStory ?? undefined,
					// petition data
					petition_id: `petition_${petition.id}`,
					organization_id: petition.organizationId ? Number(petition.organizationId) : undefined,
					ask_length: petition.ask?.length ?? 0,
					description_length: petition.description?.length ?? 0,
					geo_reach: petition.geoReach ?? undefined,
					relevant_location_localized_name: petition.relevantLocationLocalizedName ?? undefined,
					inferred_geo_scope: inferredGeoScope,
					inferred_geo_location: inferred?.location ?? undefined,
					media_type: petition.photoId ? 'photo' : 'none',
					image_type: draft?.petitionDraft?.image?.type,
					body_media_count: totalMediaCount,
					body_images: bodyImages,
					body_youtube: bodyYoutube,
					// user data
					fb_logged_in: isFbLoggedIn(utilityContext.facebook.authStatus),
					has_set_password: !!user?.passwordSet,
					login_or_join_method: draft?.metadata?.user?.loginOrJoinMethod,
					starting_sap_user_state: draft?.metadata?.user?.initialLoginState,
					// additional data
					sap_flow: 'sap_3',
					source_location: sourceLocation ?? undefined,
					view_context: 'prepublish',
					ai_title_edited: compareNormalizedStrings(petition.aiAsk, petition.ask || ''),
					ai_description_edited: compareNormalizedStrings(petition.aiDescription, petition.description || ''),
					// social starter origin — only present when the petition was created from a social starter page
					angle_token: draft?.metadata?.angleToken ?? undefined,
					user_handle: userHandle ?? undefined,
				});
			},
			trackConversion: ({ petition }) => {
				googleTagManagerTracking.push({ petition_id: petition.id, petition_title: petition.ask });
				googleTagManagerTracking.pushUserEmail(user?.email ?? '');
				googleTagManagerTracking.pushConversion('petition_publish');
			},
		}),
		[userHandle, track, user?.email, user?.passwordSet, utilityContext.facebook.authStatus],
	);
}
